import { Icon } from '@ab-inbev-maz/compass';

const MAX_VISIBLE_PAGES = 3;

// Up to 3 consecutive pages around the current one: 1 2 3, then 4 5 6 near the end...
export const getVisiblePages = (page: number, totalPages: number): number[] => {
  const count = Math.min(MAX_VISIBLE_PAGES, totalPages);
  const first = Math.min(Math.max(1, page - Math.floor(count / 2)), totalPages - count + 1);
  return Array.from({ length: count }, (_, index) => first + index);
};

// Same look as Compass's Pagination (pill group, yellow active page with an underline), reduced to
// "< 1 2 3 >" for narrow spaces like the canvas side panel, where its 10 pages and size selector don't fit.
const buttonClassName = (active = false) =>
  [
    'flex items-center justify-center min-w-14 px-4 py-3 text-[#595959] border-y border-[#1414141f] cursor-pointer',
    'first:rounded-l-full first:border-l last:rounded-r-full last:border-r',
    'disabled:bg-[#ebebeb] disabled:text-[#a6a6a6] disabled:cursor-not-allowed',
    'enabled:hover:bg-[#e5fffe] enabled:active:bg-[#fc0] enabled:active:text-[#202020]',
    active ? 'bg-[#fef1cc]! text-[#202020] shadow-[inset_0_-0.125rem_#202020]' : '',
  ].join(' ');

interface CompactPaginationProps {
  page: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  disabled?: boolean;
  className?: string;
}

const CompactPagination = ({ page, totalPages, onChangePage, disabled = false, className = '' }: CompactPaginationProps) => {
  if (totalPages <= 1) return null;

  return (
    <nav aria-label="Paginación" className={`flex justify-center ${className}`}>
      <div className="flex">
        <button
          type="button"
          aria-label="Página anterior"
          disabled={disabled || page <= 1}
          onClick={() => onChangePage(page - 1)}
          className={buttonClassName()}
        >
          <span className="flex w-6 h-6">
            <Icon name="arrow/chevron-left" />
          </span>
        </button>
        {getVisiblePages(page, totalPages).map((pageNumber) => (
          <button
            key={pageNumber}
            type="button"
            aria-current={pageNumber === page ? 'page' : undefined}
            disabled={disabled}
            onClick={() => pageNumber !== page && onChangePage(pageNumber)}
            className={buttonClassName(pageNumber === page)}
          >
            <span className="w-6 h-6 text-center">{pageNumber}</span>
          </button>
        ))}
        <button
          type="button"
          aria-label="Página siguiente"
          disabled={disabled || page >= totalPages}
          onClick={() => onChangePage(page + 1)}
          className={buttonClassName()}
        >
          <span className="flex w-6 h-6">
            <Icon name="arrow/chevron-right" />
          </span>
        </button>
      </div>
    </nav>
  );
};

export default CompactPagination;
